import { errorLogger } from "@/Common/helpers/logger";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import {
	ICreatePayPlanResponse,
	IPayPlanInput,
	IInfoInputPayPlan,
	IIdInputPayPlan,
	IPayPlanModelInputPayPlan,
	IDeleteResponse,
	IUpdatesAvailablePayPlanResponse,
} from "@/PayPlans/types/api";
import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	CustomerSatisfactionStoreFragment,
	LongevityFragment,
	CommissionTiersFragment,
	BonusDefinitionFragment,
	MetricFragment,
	InputValueFragment,
	VolumeBonusFragment,
	VariableOperationsGpsBonusSetupFragment,
} from "@PayPlans/api/fragments/commons";

import {
	CrossStoreCommissionsFragment,
	DefaultCommissionsFragment,
	GrowthBonusFragment,
	MarketShareFragment,
	PinnacleFragment,
	RankingBonusFragment,
	SFEBonusFragment,
	MarketMakeFragment,
	OperatingCostsFragment,
} from "@PayPlans/api/fragments/managers/sales";

// Create sales departmental pay plans
export async function createSalesDepartmentalPayPlan({ locationId, position }: IPayPlanInput) {
	const query = gql`
		mutation createSalesDepartmentalPayPlan($locationId: String!, $position: String!) {
			createSalesDepartmentalPayPlan(locationId: $locationId, position: $position) {
				name
				createdAt
				formatVersion
			}
		}
	`;

	const variables = { locationId, position };

	try {
		const response: GraphqlQueryResponse<"createSalesDepartmentalPayPlan", ICreatePayPlanResponse> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.createSalesDepartmentalPayPlan || null;
	} catch (error) {
		return error;
	}
}
// Update available sales departmental pay plans
export async function updateSalesDepartmentalAvailablePayPlan({ locationId, information, position }: IInfoInputPayPlan) {
	const { _id, updatedBy, available } = information;
	const query = gql`
		mutation updateSalesDepartmentalAvailablePayPlan($locationId: String!, $input: UpdateAvailablePayPlanInput!, $position: String!) {
			updateSalesDepartmentalAvailablePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
		input: {
			_id,
			updatedBy,
			available,
		},
	};

	try {
		const response: GraphqlQueryResponse<"updateSalesDepartmentalAvailablePayPlan", IUpdatesAvailablePayPlanResponse> =
			await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSalesDepartmentalAvailablePayPlan;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
// Add new sales departmental pay plans
export async function cloneSalesDepartmentalPayPlan({ locationId, information, actualPosition }: IInfoInputPayPlan) {
	const { _id, createdBy, position } = information;
	const query = gql`
		mutation cloneSalesDepartmentalPayPlan($locationId: String!, $input: ClonePayPlan!, $position: String!) {
			cloneSalesDepartmentalPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				crossStoreCommissions {
					...CrossStoreCommissions
				}
				volumeBonus {
					...VolumeBonus
				}
				rankingBonus {
					...RankingBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				marketShare {
					...MarketShare
				}
				growthBonus {
					...GrowthBonus
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				sfe {
					...SFEBonus
				}
				pinnacle {
					...Pinnacle
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${CrossStoreCommissionsFragment}
		${DefaultCommissionsFragment}
		${VolumeBonusFragment}
		${RankingBonusFragment}
		${CallActivityPerformanceFragment}
		${MarketShareFragment}
		${GrowthBonusFragment}
		${CustomerSatisfactionStoreFragment}
		${SFEBonusFragment}
		${PinnacleFragment}
		${LongevityFragment}
		${CommissionTiersFragment}
		${BonusDefinitionFragment}
		${MarketMakeFragment}
		${MetricFragment}
		${InputValueFragment}
		${OperatingCostsFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		input: {
			payPlanId: _id,
			createdBy,
			position,
		},
		position: actualPosition,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"cloneSalesDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.cloneSalesDepartmentalPayPlan || null;
	} catch (error) {
		return error;
	}
}
// Delete selected sales departmental pay plans
export async function deleteSalesDepartmentalPayPlan({ locationId, _id, position }: IIdInputPayPlan) {
	const query = gql`
		mutation deleteSalesDepartmentalPayPlan($locationId: String!, $id: String!, $position: String!) {
			deleteSalesDepartmentalPayPlan(id: $id, locationId: $locationId, position: $position) {
				_id
				locationId
			}
		}
	`;

	const variables = {
		id: _id,
		position,
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"deleteSalesDepartmentalPayPlan", IDeleteResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.deleteSalesDepartmentalPayPlan || null;
	} catch (error) {
		return error;
	}
}
// Update selected sales departmental pay plan
export async function updateSalesDepartmentalPayPlan({ locationId, payPlan, position }: IPayPlanModelInputPayPlan) {
	const query = gql`
		mutation updateSalesDepartmentalPayPlan($locationId: String!, $input: UpdateSalesDepartmentalPayPlan!, $position: String!) {
			updateSalesDepartmentalPayPlan(input: $input, locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				crossStoreCommissions {
					...CrossStoreCommissions
				}
				volumeBonus {
					...VolumeBonus
				}
				rankingBonus {
					...RankingBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				marketShare {
					...MarketShare
				}
				growthBonus {
					...GrowthBonus
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				sfe {
					...SFEBonus
				}
				pinnacle {
					...Pinnacle
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${CrossStoreCommissionsFragment}
		${DefaultCommissionsFragment}
		${VolumeBonusFragment}
		${RankingBonusFragment}
		${CallActivityPerformanceFragment}
		${MarketShareFragment}
		${GrowthBonusFragment}
		${CustomerSatisfactionStoreFragment}
		${SFEBonusFragment}
		${PinnacleFragment}
		${LongevityFragment}
		${CommissionTiersFragment}
		${BonusDefinitionFragment}
		${MarketMakeFragment}
		${MetricFragment}
		${InputValueFragment}
		${OperatingCostsFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		input: payPlan,
		locationId,
		position,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"updateSalesDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateSalesDepartmentalPayPlan || null;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
// save new selected sales departmental pay plan
export async function saveNewSalesDepartmentalPayPlan({ locationId, payPlan, position }: IPayPlanModelInputPayPlan) {
	const query = gql`
		mutation saveNewSalesDepartmentalPayPlan($locationId: String!, $input: SaveSalesDepartmentalPayPlan!, $position: String!) {
			saveNewSalesDepartmentalPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				crossStoreCommissions {
					...CrossStoreCommissions
				}
				volumeBonus {
					...VolumeBonus
				}
				rankingBonus {
					...RankingBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				marketShare {
					...MarketShare
				}
				growthBonus {
					...GrowthBonus
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				sfe {
					...SFEBonus
				}
				pinnacle {
					...Pinnacle
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${CrossStoreCommissionsFragment}
		${DefaultCommissionsFragment}
		${VolumeBonusFragment}
		${RankingBonusFragment}
		${CallActivityPerformanceFragment}
		${MarketShareFragment}
		${GrowthBonusFragment}
		${CustomerSatisfactionStoreFragment}
		${SFEBonusFragment}
		${PinnacleFragment}
		${LongevityFragment}
		${CommissionTiersFragment}
		${BonusDefinitionFragment}
		${MarketMakeFragment}
		${MetricFragment}
		${InputValueFragment}
		${OperatingCostsFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		position,
		input: payPlan,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"saveNewSalesDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.saveNewSalesDepartmentalPayPlan;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
